import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import CreateButton from "../../UI/CreateButton";
import Modal from "../../UI/Modal";
import CreateDogovor from "./CreateDogovor";
import ActionsForDogovor from "./ActionsForDogovor";
import {
  useGetDogovorsQuery,
  useUpdateDogovorMutation,
} from "../../../store/dogovorApi";
import { useGetOrganizationQuery } from "../../../store/organizationApi";
import { convertToRuFormat } from "../../../utilits/helpers/convertDateFormat";
import { useGetPaymentCodeQuery } from "../../../store/paymentCodeApi";
import { InputAdornment, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FlexContainer from "../../UI/FlexContainer";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "docDate",
    numeric: false,
    disablePadding: true,
    label: "Дата документа",
  },
  {
    id: "numberDogovor",
    numeric: false,
    disablePadding: false,
    label: "№ договора",
  },
  {
    id: "contragent",
    numeric: false,
    disablePadding: false,
    label: "Контрагент",
  },
  {
    id: "nomenklatura",
    numeric: false,
    disablePadding: false,
    label: "Номенклатура",
  },
  {
    id: "autor",
    numeric: false,
    disablePadding: false,
    label: "Автор",
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: "Действие",
  },
];

function EnhancedTableHead(props) {
  const {
    // onSelectAllClick,
    order,
    orderBy,
    // numSelected,
    // rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          {/* <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            //checked={rowCount > 0 && numSelected === rowCount}
            // onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          /> */}
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Договора
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export default function Dogovora({ getDogovorItem, onCloseDogovor }) {
  const { data } = useGetDogovorsQuery(
    "?sort[0]=id:desc&populate[1]=user&populate[2]=nomenklatura.k2&populate[3]=contragent"
  );
  const { data: organizFromApi } = useGetOrganizationQuery();
  const { data: paymentCodeFB } = useGetPaymentCodeQuery("?sort[0]=id:asc");
  const [updateData] = useUpdateDogovorMutation();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [organization, setOrganization] = React.useState("");
  const [paymentCodes, setPaymentCodes] = React.useState([]);

  React.useEffect(() => {
    const setDataToStates = async () => {
      try {
        if (data && data.data) {
          const dogovors = await data.data;
          const org = await organizFromApi.data;
          const paymentCode = await paymentCodeFB.data;
          setRows(dogovors);
          setFilteredRows(dogovors); // Initialize filteredRows
          setOrganization(org);
          setPaymentCodes(paymentCode);
        }
      } catch (error) {
        console.error("Error setting data to states:", error);
      }
    };
    setDataToStates();
  }, [data, organizFromApi, paymentCodeFB]);

  React.useEffect(() => {
    const lowercasedSearch = search.toLowerCase();
    if (lowercasedSearch === "") {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter((item) => {
        const numberDogovor = item.attributes.numberDogovor ? item.attributes.numberDogovor.toLowerCase() : "";
        const contragentFullName =
          item.attributes.contragent?.data[0]?.attributes ?
          `${item.attributes.contragent.data[0].attributes.surname} ${item.attributes.contragent.data[0].attributes.name} ${item.attributes.contragent.data[0].attributes.lastname}`.toLowerCase() :
          "";
        const nomenklaturaName = item.attributes.nomenklatura?.data[0]?.attributes?.name ?
          item.attributes.nomenklatura.data[0].attributes.name.toLowerCase() :
          "";
        const userName = item.attributes.user?.data[0]?.attributes?.username ?
          item.attributes.user.data[0].attributes.username.toLowerCase() :
          "";

        return (
          numberDogovor.includes(lowercasedSearch) ||
          contragentFullName.includes(lowercasedSearch) ||
          nomenklaturaName.includes(lowercasedSearch) ||
          userName.includes(lowercasedSearch)
        );
      });
      setFilteredRows(filtered);
    }
  }, [search, rows]);

  const updateChckedRow = (updatedRow, checkedValue) => {
    const updated = { ...updatedRow.attributes, checked: checkedValue };
    updateData({ updated, id: updatedRow.id });
  };

  const clickSelectRowHandler = (item) => {
    if (typeof getDogovorItem === "function") {
      getDogovorItem(item);
      onCloseDogovor();
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredRows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  // const handleClick = (event, name) => {
  //   if (!event.target.disabled) {
  //     // Добавлено условие, чтобы игнорировать клики, когда чекбокс отключен
  //     const selectedIndex = selected.indexOf(name);
  //     let newSelected = [];

  //     if (selectedIndex === -1) {
  //       newSelected = newSelected.concat(selected, name);
  //     } else if (selectedIndex === 0) {
  //       newSelected = newSelected.concat(selected.slice(1));
  //     } else if (selectedIndex === selected.length - 1) {
  //       newSelected = newSelected.concat(selected.slice(0, -1));
  //     } else if (selectedIndex > 0) {
  //       newSelected = newSelected.concat(
  //         selected.slice(0, selectedIndex),
  //         selected.slice(selectedIndex + 1)
  //       );
  //     }

  //     setSelected(newSelected);
  //   }
  // };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <FlexContainer width="600px" justifyContent="space-between">
        <CreateButton onClick={() => setCreateModalOpen(true)} />
        <TextField
          id="outlined-basic"
          label="Поиск"
          variant="outlined"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ width: '370px', marginTop: '15px' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {search && (
                  <IconButton onClick={() => setSearch("")} edge="end">
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </FlexContainer>
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        fullwidth={"60%"}
      >
        <CreateDogovor onClose={() => setCreateModalOpen(false)} />
      </Modal>
      <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filteredRows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.name);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    onClick={() => clickSelectRowHandler(row)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={false}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={row.attributes.checked}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                        style={{ pointerEvents: "none" }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {convertToRuFormat(row.attributes.docDate)}
                    </TableCell>
                    <TableCell align="left">
                      {row.attributes.numberDogovor}
                    </TableCell>
                    <TableCell align="left">
                      {row.attributes.contragent.data[0].attributes.surname +
                        " " +
                        row.attributes.contragent.data[0].attributes.name +
                        " " +
                        row.attributes.contragent.data[0].attributes.lastname}
                    </TableCell>
                    <TableCell align="left">
                      {row.attributes.nomenklatura.data[0].attributes?.name}
                    </TableCell>
                    <TableCell align="left">
                      {row.attributes.user.data[0].attributes.username}
                    </TableCell>
                    <TableCell align="left">
                      <ActionsForDogovor
                        item={row}
                        updateChckedRow={updateChckedRow}
                        organization={organization}
                        paymentCodes={paymentCodes}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={"Элементов на странице"}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
}
